/* eslint-disable react/prop-types */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Spin, notification, message } from 'antd';
import {
  fetchAiAdminSettings,
  saveAiAdminSettings,
  fetchBedrockModels,
  testBedrockModel,
} from '../../services/aiInsightsApi';
import AdminAiSelect from './AdminAiSelect';
import './AdminAiPages.css';

const PROVIDERS = [
  { value: 'none', label: 'None (disabled)' },
  { value: 'bedrock', label: 'AWS Bedrock (execution role, no API key)' },
];

/** Always offered even when ListInferenceProfiles fails (Lessons Learned sister). */
const CURATED_MODELS = [
  { id: 'us.anthropic.claude-haiku-4-5-20251001-v1:0', name: 'Claude Haiku 4.5' },
  { id: 'us.anthropic.claude-sonnet-4-6', name: 'Claude Sonnet 4.6' },
  { id: 'us.anthropic.claude-opus-4-5-20251101-v1:0', name: 'Claude Opus 4.5' },
  { id: 'us.anthropic.claude-opus-4-6-v1', name: 'Claude Opus 4.6' },
];

const parseList = (val) => {
  if (Array.isArray(val)) return val.map(String);
  if (typeof val !== 'string' || !val) return [];
  try {
    const parsed = JSON.parse(val);
    return Array.isArray(parsed) ? parsed.map(String) : [];
  } catch {
    return val
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
  }
};

const mergeModels = (live = []) => {
  const byId = new Map();
  live.forEach((m) => {
    if (m?.id) byId.set(m.id, { id: m.id, name: m.name || m.id });
  });
  CURATED_MODELS.forEach((m) => byId.set(m.id, m));
  return Array.from(byId.values()).sort((a, b) => a.name.localeCompare(b.name));
};

const optionLabel = (m) => `${m.name} — ${m.id}`;

/**
 * Admin AI Configuration — Lessons Learned sister (native light form; avoids global antd dark styles).
 */
const AdminAiSettingsPage = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [models, setModels] = useState(() => mergeModels([]));
  const [modelsLoading, setModelsLoading] = useState(false);
  const [listError, setListError] = useState('');

  const [aiProvider, setAiProvider] = useState('bedrock');
  const [bedrockRegion, setBedrockRegion] = useState('us-east-1');
  const [bedrockModelId, setBedrockModelId] = useState('');
  const [classifyModelId, setClassifyModelId] = useState('');
  const [quotaRecipients, setQuotaRecipients] = useState('');
  const [askEvansEnabled, setAskEvansEnabled] = useState(true);
  const [askEvansEmails, setAskEvansEmails] = useState('');
  const [runtime, setRuntime] = useState(null);

  const loadModels = useCallback(async (region) => {
    const where = String(region || '').trim();
    if (!where) {
      setModels(mergeModels([]));
      return;
    }
    setModelsLoading(true);
    setListError('');
    try {
      const data = await fetchBedrockModels(where);
      const live = Array.isArray(data.models) ? data.models : [];
      setModels(mergeModels(live));
      if (live.length === 0 && data.reason) {
        setListError(`${data.reason} Showing curated Haiku / Sonnet / Opus options instead.`);
      }
    } catch (err) {
      setModels(mergeModels([]));
      setListError(
        `${err?.message || 'The model list could not be read.'} Showing curated options.`
      );
    } finally {
      setModelsLoading(false);
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchAiAdminSettings();
      const s = data.settings || {};
      setAiProvider(String(s.ai_provider || 'bedrock'));
      const region = String(s.ai_bedrock_region || 'us-east-1');
      setBedrockRegion(region);
      setBedrockModelId(String(s.ai_bedrock_model_id || ''));
      setClassifyModelId(String(s.ai_bedrock_classify_model_id || ''));
      setQuotaRecipients(parseList(s.ai_quota_admin_recipients).join(', '));
      const sw = s.ai_feature_ask_evans_enabled;
      setAskEvansEnabled(
        sw == null || sw === ''
          ? true
          : !['false', '0', 'off'].includes(String(sw).toLowerCase())
      );
      setAskEvansEmails(parseList(s.ai_feature_ask_evans_emails).join(', '));
      setRuntime(data.runtime || null);
      await loadModels(region);
    } catch (err) {
      notification.error({
        message: 'Failed to load AI settings',
        description: String(err?.message || err),
      });
    } finally {
      setLoading(false);
    }
  }, [loadModels]);

  useEffect(() => {
    load();
  }, [load]);

  const modelOptions = useMemo(() => {
    const opts = models.map((m) => ({ value: m.id, label: optionLabel(m) }));
    // Keep a saved id visible even if it is not in the live/curated list
    [bedrockModelId, classifyModelId].forEach((id) => {
      if (id && !opts.some((o) => o.value === id)) {
        opts.unshift({ value: id, label: id });
      }
    });
    return opts;
  }, [models, bedrockModelId, classifyModelId]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const data = await saveAiAdminSettings({
        ai_provider: aiProvider,
        ai_bedrock_region: bedrockRegion,
        ai_bedrock_model_id: bedrockModelId,
        ai_bedrock_classify_model_id: classifyModelId || bedrockModelId,
        ai_quota_admin_recipients: parseList(quotaRecipients),
        ai_feature_ask_evans_enabled: askEvansEnabled,
        ai_feature_ask_evans_emails: parseList(askEvansEmails),
      });
      setRuntime(data.runtime || null);
      message.success('AI settings saved');
    } catch (err) {
      notification.error({
        message: 'Save failed',
        description: String(err?.message || err),
      });
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const result = await testBedrockModel({
        modelId: bedrockModelId,
        region: bedrockRegion,
      });
      setTestResult(result);
      if (result.ok) message.success(result.message || 'Model test succeeded');
    } catch (err) {
      setTestResult({ ok: false, message: String(err?.message || err) });
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-ai-page admin-ai-page--loading">
        <Spin />
      </div>
    );
  }

  const isBedrock = aiProvider === 'bedrock';

  return (
    <div className="admin-ai-page">
      <header className="admin-ai-page__header">
        <div>
          <h2 className="admin-ai-page__title">AI Configuration</h2>
          <p className="admin-ai-page__sub">
            Configure AI provider for Spacer Log Ask Evans insights and recommendations.
          </p>
        </div>
        <button
          type="button"
          className="admin-ai-btn admin-ai-btn--primary"
          disabled={saving}
          onClick={handleSave}
        >
          {saving ? 'Saving…' : 'Save settings'}
        </button>
      </header>

      <section className="admin-ai-card">
        <h3 className="admin-ai-card__title">AI Provider Settings</h3>

        <div className="admin-ai-field">
          <span className="admin-ai-label">AI Provider</span>
          <div className="admin-ai-radios" role="radiogroup" aria-label="AI Provider">
            {PROVIDERS.map((p) => (
              <label key={p.value} className="admin-ai-radio">
                <input
                  type="radio"
                  name="ai_provider"
                  value={p.value}
                  checked={aiProvider === p.value}
                  onChange={() => setAiProvider(p.value)}
                  disabled={saving}
                />
                <span>{p.label}</span>
              </label>
            ))}
          </div>
        </div>

        {isBedrock ? (
          <>
            <div className="admin-ai-field">
              <label className="admin-ai-label" htmlFor="ai_bedrock_region">
                Bedrock region
              </label>
              <input
                id="ai_bedrock_region"
                type="text"
                className="admin-ai-input"
                value={bedrockRegion}
                onChange={(e) => setBedrockRegion(e.target.value)}
                onBlur={() => loadModels(bedrockRegion)}
                placeholder="us-east-1"
                disabled={saving}
              />
            </div>

            <div className="admin-ai-field">
              <AdminAiSelect
                label="Reasoning model (Ask Evans, Spacer Log briefs)"
                options={[
                  {
                    value: '',
                    label: modelsLoading
                      ? 'Reading models in this region…'
                      : 'Choose a model',
                  },
                  ...modelOptions,
                ]}
                value={bedrockModelId}
                onChange={(v) => {
                  setBedrockModelId(v);
                  setTestResult(null);
                }}
                placeholder={
                  modelsLoading ? 'Reading models in this region…' : 'Choose a model'
                }
                disabled={saving || modelsLoading}
                searchable
              />
              <p className="admin-ai-hint">
                Used for Ask Evans and Spacer Log briefs. Approx. Bedrock cost depends on the
                selected model (e.g. Haiku 4.5 ~$1/M input, $5/M output).
              </p>
            </div>

            <div className="admin-ai-field">
              <AdminAiSelect
                label="Classification model (optional)"
                options={[
                  {
                    value: '',
                    label: modelsLoading
                      ? 'Reading models in this region…'
                      : 'Same as the reasoning model',
                  },
                  ...modelOptions,
                ]}
                value={classifyModelId}
                onChange={setClassifyModelId}
                placeholder={
                  modelsLoading
                    ? 'Reading models in this region…'
                    : 'Same as the reasoning model'
                }
                disabled={saving || modelsLoading}
                searchable
              />
              <p className="admin-ai-hint">
                Optional cheaper model (e.g. Haiku) for light classification tasks.
              </p>
            </div>

            {listError ? <p className="admin-ai-warn">{listError}</p> : null}

            <div className="admin-ai-test-row">
              <button
                type="button"
                className="admin-ai-btn admin-ai-btn--ghost"
                onClick={handleTest}
                disabled={!bedrockModelId || !bedrockRegion || testing || saving}
              >
                {testing ? 'Testing…' : 'Test this model'}
              </button>
              {testResult ? (
                <span
                  className={`admin-ai-test-msg${
                    testResult.ok ? ' admin-ai-test-msg--ok' : ' admin-ai-test-msg--err'
                  }`}
                >
                  {testResult.ok ? 'Works. ' : 'Failed. '}
                  {testResult.message}
                </span>
              ) : null}
            </div>
          </>
        ) : null}

        <div className="admin-ai-field" style={{ marginTop: 20 }}>
          <label className="admin-ai-label" htmlFor="ai_quota_recipients">
            Quota alert recipients
          </label>
          <input
            id="ai_quota_recipients"
            type="text"
            className="admin-ai-input"
            value={quotaRecipients}
            onChange={(e) => setQuotaRecipients(e.target.value)}
            placeholder="qhse.lead@crce.com, director@crce.com"
            disabled={saving}
          />
          <p className="admin-ai-hint">
            Comma-separated emails notified when monthly Ask Evans token usage approaches the
            limit.
          </p>
        </div>

        <div className="admin-ai-field admin-ai-field--row">
          <div>
            <span className="admin-ai-label">Ask Evans enabled</span>
            <p className="admin-ai-hint" style={{ marginBottom: 0 }}>
              Kill switch for Spacer Log Ask Evans chat and insights.
            </p>
          </div>
          <label className="admin-ai-switch">
            <input
              type="checkbox"
              checked={askEvansEnabled}
              onChange={(e) => setAskEvansEnabled(e.target.checked)}
              disabled={saving}
            />
            <span className="admin-ai-switch__track" aria-hidden />
          </label>
        </div>

        <div className="admin-ai-field">
          <label className="admin-ai-label" htmlFor="ai_ask_evans_emails">
            Ask Evans audience emails (optional)
          </label>
          <textarea
            id="ai_ask_evans_emails"
            className="admin-ai-input admin-ai-textarea"
            rows={2}
            value={askEvansEmails}
            onChange={(e) => setAskEvansEmails(e.target.value)}
            placeholder="Leave empty to allow all authenticated users"
            disabled={saving}
          />
        </div>

        {runtime ? (
          <div className="admin-ai-runtime">
            Runtime: {runtime.enabled ? 'enabled' : 'disabled'} · {runtime.provider} ·{' '}
            {runtime.bedrockModelId} · {runtime.bedrockRegion}
            {runtime.ragConfigured ? ' · RAG configured' : ' · RAG not configured'}
          </div>
        ) : null}
      </section>
    </div>
  );
};

export default AdminAiSettingsPage;
